import { AtSignIcon, EmptyState } from '@buffer-mono/popcorn'

import { getChannelName } from '~publish/helpers/channels'
import { ChannelRefreshCard } from '~publish/pages/Community/components/ChannelRefresh/ChannelRefreshCard'
import type { RefreshableChannel } from '~publish/pages/Community/components/ChannelRefresh/ChannelRefreshCard'

import styles from './MentionsNeedsRefreshEmptyState.module.css'

import type { JSX } from 'react'

type MentionsNeedsRefreshEmptyStateProps = {
  channels: RefreshableChannel[]
}

/**
 * Stands in for the mentions feed when every channel in scope must reconnect
 * before Buffer can collect mentions. The connection lacks the permission the
 * feature needs — never granted, or lost since. The copy stays neutral on
 * which, and the state frames the refresh as unlocking mentions rather than
 * repairing a broken connection.
 */
export function MentionsNeedsRefreshEmptyState({
  channels,
}: MentionsNeedsRefreshEmptyStateProps): JSX.Element {
  // A multi-channel scope can mix services, so only a single blocked channel
  // gets its service named in the copy.
  const onlyChannel = channels.length === 1 ? channels[0] : undefined

  return (
    <div className={styles.layoutWrapper}>
      <EmptyState size="large">
        <EmptyState.Icon>
          <AtSignIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>
          {onlyChannel
            ? 'Unlock mentions for this channel'
            : 'Unlock mentions for these channels'}
        </EmptyState.Heading>
        <EmptyState.Description>
          {onlyChannel
            ? `To access ${getChannelName(
                onlyChannel.service,
              )} mentions, refresh your channel connection.`
            : 'To access mentions, refresh your channel connections.'}
        </EmptyState.Description>
        <EmptyState.Actions className={styles.actions}>
          {channels.map((channel) => (
            <ChannelRefreshCard
              key={channel.id}
              channel={channel}
              cta="publish-mentions-button-refresh-1"
            />
          ))}
        </EmptyState.Actions>
      </EmptyState>
    </div>
  )
}
